<template>
  <v-dialog :model-value="modelValue" max-width="480" :persistent="busy" @update:model-value="emit('update:modelValue', $event)">
    <v-card class="wiz">
      <!-- Header -->
      <div class="wiz-head">
        <div class="step-ic">
          <v-icon>{{ STEP_META[step].icon }}</v-icon>
        </div>
        <div class="t">{{ t(STEP_META[step].titleKey) }}</div>
        <v-btn icon="mdi-close" variant="text" size="small" :disabled="busy" @click="close" />
      </div>

      <!-- Progress dots -->
      <div class="dots">
        <div v-for="i in 4" :key="i" class="d" :class="{ on: (i - 1) <= step }" />
      </div>

      <!-- Body -->
      <div class="wiz-body">
        <!-- ── STEP 0: PHONE (+ instructor in admin mode) ── -->
        <template v-if="step === 0">
          <template v-if="instructors.length">
            <div class="label">{{ t('schedule.wizard.instructorLabel') }}</div>
            <v-select
              v-model="form.instructorId"
              :items="instructors"
              item-title="fio"
              item-value="id"
              density="comfortable"
              variant="outlined"
              hide-details
              class="mb-4"
              prepend-inner-icon="mdi-account-tie"
            />
          </template>

          <input
            ref="phoneEl"
            class="big-input"
            inputmode="tel"
            placeholder="+7 ___ ___ __ __"
            :value="form.phone"
            @input="onPhoneInput($event.target.value)"
          />

          <div v-if="phoneDigits.length >= 11" class="mt-3">
            <v-alert v-if="matchedStudent" type="success" variant="tonal" density="comfortable" icon="mdi-account-check">
              <strong>{{ matchedStudent.fio }}</strong>
            </v-alert>
            <v-alert v-else type="warning" variant="tonal" density="comfortable" icon="mdi-account-plus">
              <div class="text-body-2 mb-2">{{ t('schedule.wizard.newClient') }}</div>
              <v-text-field
                v-model="form.fio"
                :placeholder="t('schedule.wizard.clientNamePlaceholder')"
                density="comfortable"
                variant="solo"
                flat
                hide-details
                bg-color="surface"
                autofocus
              />
            </v-alert>
          </div>
        </template>

        <!-- ── STEP 1: TIME + DATE ── -->
        <template v-else-if="step === 1">
          <div class="label">{{ t('schedule.wizard.pickTime') }}</div>
          <!-- Time pickers: start + end -->
          <div class="time-row">
            <div class="time-col">
              <div class="label">{{ t('schedule.wizard.timeStart') }}</div>
              <input
                class="time-input"
                type="time"
                :value="slotStart"
                @change="onStartChange($event.target.value)"
              />
            </div>
            <div class="time-sep">—</div>
            <div class="time-col">
              <div class="label">{{ t('schedule.wizard.timeEnd') }}</div>
              <input
                class="time-input"
                type="time"
                :value="slotEnd"
                @change="onEndChange($event.target.value)"
              />
            </div>
          </div>

          <!-- Conflict warning -->
          <div v-if="form.timeSlot && takenSlots.has(form.timeSlot)" class="conflict-warn">
            <v-icon size="16" color="error">mdi-lock</v-icon>
            {{ t('schedule.wizard.slotTaken') }}
          </div>

          <!-- Quick presets -->
          <div v-if="timeSlots.length" class="label mt-3">{{ t('schedule.wizard.quickPick') }}</div>
          <div v-if="timeSlots.length" class="preset-chips">
            <button
              v-for="slot in timeSlots"
              :key="slot"
              type="button"
              class="preset-chip"
              :class="{ on: form.timeSlot === slot, taken: takenSlots.has(slot) }"
              :disabled="takenSlots.has(slot)"
              @click="applyPreset(slot)"
            >
              <v-icon v-if="takenSlots.has(slot)" size="11" start>mdi-lock</v-icon>
              {{ slot.split('-')[0] }}
            </button>
          </div>

          <div class="label mt-4">{{ t('schedule.wizard.date') }}</div>
          <input class="date-input" type="date" :min="todayIso" :value="form.date" @input="form.date = $event.target.value" />
        </template>

        <!-- ── STEP 2: ADDRESS ── -->
        <template v-else-if="step === 2">
          <div class="label">{{ t('schedule.wizard.pickAddress') }}</div>
          <div class="addr-list">
            <button
              v-for="a in addresses"
              :key="a.id"
              type="button"
              class="addr-item"
              :class="{ on: form.addressId === a.id }"
              @click="form.addressId = (form.addressId === a.id ? null : a.id)"
            >
              <v-icon size="18" :color="form.addressId === a.id ? 'primary' : ''">mdi-map-marker</v-icon>
              <span>{{ a.address }}</span>
              <v-icon v-if="form.addressId === a.id" size="18" color="primary" class="ms-auto">mdi-check</v-icon>
            </button>

            <div v-if="!addresses.length && !showAddAddr" class="text-caption text-medium-emphasis py-2">
              {{ t('schedule.wizard.noAddresses') }}
            </div>

            <!-- Inline add address (instructor) -->
            <template v-if="allowAddAddress">
              <button v-if="!showAddAddr" type="button" class="addr-item addr-add" @click="showAddAddr = true">
                <v-icon size="18" color="primary">mdi-plus</v-icon>
                <span>{{ t('schedule.wizard.addAddress') }}</span>
              </button>
              <div v-else class="add-addr-panel">
                <v-text-field
                  v-model="newAddr.address"
                  :label="t('schedule.wizard.addressField')"
                  density="compact" variant="outlined" hide-details class="mb-2"
                  prepend-inner-icon="mdi-map-marker"
                />
                <v-text-field
                  v-model="newAddr.name"
                  :label="t('schedule.wizard.addressName')"
                  density="compact" variant="outlined" hide-details class="mb-2"
                  prepend-inner-icon="mdi-tag-outline"
                />
                <div class="d-flex ga-2">
                  <v-btn size="small" variant="text" @click="showAddAddr = false">{{ t('common.cancel') }}</v-btn>
                  <v-spacer />
                  <v-btn size="small" color="primary" :loading="addingAddr" :disabled="!newAddr.address.trim()" @click="submitNewAddress">
                    {{ t('schedule.wizard.save') }}
                  </v-btn>
                </div>
              </div>
            </template>
          </div>
        </template>

        <!-- ── STEP 3: NOTE ── -->
        <template v-else-if="step === 3">
          <div class="label">{{ t('schedule.wizard.noteHint') }}</div>
          <textarea
            class="note-input"
            :placeholder="t('schedule.wizard.notePlaceholder')"
            :value="form.note"
            @input="form.note = $event.target.value"
          />
        </template>
      </div>


      <!-- Footer nav -->
      <div class="wiz-foot">
        <v-btn v-if="step > 0" variant="tonal" size="large" :disabled="busy" @click="back">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn
          v-if="step < 3"
          color="primary"
          size="large"
          class="flex-grow-1"
          :disabled="!canNext"
          @click="next"
        >
          {{ t('schedule.wizard.next') }}
          <v-icon end>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="success"
          size="large"
          class="flex-grow-1"
          :loading="busy"
          @click="submit"
        >
          <v-icon start>mdi-check</v-icon>
          {{ t('schedule.wizard.saveLesson') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  students: { type: Array, default: () => [] },          // [{id, fio, phone}]
  timeSlots: { type: Array, default: () => [] },          // ['08:00-09:00', ...]
  addresses: { type: Array, default: () => [] },          // [{id, address, name}]
  bookedLessons: { type: Array, default: () => [] },      // [{dateIso, timeSlot, status, instructorId}]
  instructors: { type: Array, default: () => [] },        // admin mode: [{id, fio}]
  defaultInstructorId: { type: [Number, null], default: null },
  defaultDate: { type: String, default: '' },
  allowAddAddress: { type: Boolean, default: false },
  busy: { type: Boolean, default: false }
})
const emit = defineEmits(['update:modelValue', 'submit', 'add-address'])

const { t } = useI18n()

const STEP_META = [
  { icon: 'mdi-phone', titleKey: 'schedule.wizard.step1' },
  { icon: 'mdi-clock-outline', titleKey: 'schedule.wizard.step2' },
  { icon: 'mdi-map-marker', titleKey: 'schedule.wizard.step3' },
  { icon: 'mdi-note-text-outline', titleKey: 'schedule.wizard.step4' }
]

const step = ref(0)
const phoneEl = ref(null)
const showAddAddr = ref(false)
const addingAddr = ref(false)
const newAddr = reactive({ address: '', name: '' })

const form = reactive({
  instructorId: null,
  phone: '+7',
  fio: '',
  studentId: null,
  date: '',
  timeSlot: '',
  addressId: null,
  note: ''
})

const toLocalIso = (d) => {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
const todayIso = computed(() => toLocalIso(new Date()))

// ── Phone ──
const formatPhoneKz = (value) => {
  let digits = String(value || '').replace(/\D/g, '')
  if (digits.startsWith('8')) digits = `7${digits.slice(1)}`
  if (digits.startsWith('7')) digits = digits.slice(1)
  digits = digits.slice(0, 10)
  const parts = ['+7']
  if (digits.length > 0) parts.push(digits.slice(0, 3))
  if (digits.length > 3) parts.push(digits.slice(3, 6))
  if (digits.length > 6) parts.push(digits.slice(6, 8))
  if (digits.length > 8) parts.push(digits.slice(8, 10))
  return parts.join(' ')
}
const onPhoneInput = (val) => { form.phone = formatPhoneKz(val) }
const phoneDigits = computed(() => form.phone.replace(/\D/g, ''))

// Canonical compact phone for storage: +7XXXXXXXXXX (no spaces)
const compactPhone = (p) => {
  let d = String(p || '').replace(/\D/g, '')
  if (d.startsWith('8')) d = '7' + d.slice(1)
  if (d.length === 10) d = '7' + d
  if (d.startsWith('7')) d = d.slice(0, 11)
  return d.length === 11 ? `+${d}` : String(p || '').trim()
}

const last10 = (p) => {
  const d = String(p || '').replace(/\D/g, '')
  return d.length >= 10 ? d.slice(-10) : d
}
const matchedStudent = computed(() => {
  if (phoneDigits.value.length < 11) return null
  const key = last10(form.phone)
  return props.students.find(s => last10(s.phone) === key) || null
})
// keep studentId/fio in sync with match
watch(matchedStudent, (m) => {
  if (m) { form.studentId = m.id; form.fio = m.fio }
  else { form.studentId = null }
})

const displayName = computed(() => matchedStudent.value?.fio || form.fio || '')

// ── Time ──
const takenSlots = computed(() => {
  const set = new Set()
  for (const l of props.bookedLessons) {
    if (l.status === 'CANCELLED') continue
    if (l.dateIso !== form.date) continue
    if (props.instructors.length && form.instructorId && l.instructorId && l.instructorId !== form.instructorId) continue
    if (l.timeSlot) set.add(l.timeSlot)
  }
  return set
})

const minsToTime = (m) => {
  const total = ((m % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}
const timeToMins = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m }

// Derived start/end from form.timeSlot
const slotStart = computed(() => form.timeSlot ? form.timeSlot.split('-')[0] : '')
const slotEnd   = computed(() => form.timeSlot ? form.timeSlot.split('-')[1] || '' : '')

const onStartChange = (val) => {
  if (!val) return
  const endVal = slotEnd.value || minsToTime(timeToMins(val) + 60)
  form.timeSlot = `${val}-${endVal}`
}
const onEndChange = (val) => {
  if (!val) return
  const startVal = slotStart.value || '08:00'
  form.timeSlot = `${startVal}-${val}`
}
const applyPreset = (slot) => { if (!takenSlots.value.has(slot)) form.timeSlot = slot }

// ── Address ──
const selectedAddressText = computed(() => {
  const a = props.addresses.find(x => x.id === form.addressId)
  if (!a) return ''
  return a.address
})
const submitNewAddress = () => {
  if (!newAddr.address.trim()) return
  addingAddr.value = true
  emit('add-address', { address: newAddr.address.trim(), name: newAddr.name.trim() }, (created) => {
    addingAddr.value = false
    if (created?.id) {
      form.addressId = created.id
      newAddr.address = ''; newAddr.name = ''
      showAddAddr.value = false
    }
  })
}

// ── Preview ──
const hasPreview = computed(() => form.timeSlot || displayName.value || (form.phone && phoneDigits.value.length > 2) || form.note)

// ── Navigation ──
const canNext = computed(() => {
  if (step.value === 0) {
    if (props.instructors.length && !form.instructorId) return false
    if (phoneDigits.value.length < 11) return false
    return Boolean(form.studentId || form.fio.trim())
  }
  if (step.value === 1) return Boolean(form.timeSlot && form.date)
  // Address required (unless none exist at all)
  if (step.value === 2) return Boolean(form.addressId) || props.addresses.length === 0
  return true
})
const next = () => { if (canNext.value && step.value < 3) step.value++ }
const back = () => { if (step.value > 0) step.value-- }

const close = () => emit('update:modelValue', false)

const submit = () => {
  emit('submit', {
    instructorId: form.instructorId,
    studentId: form.studentId,
    fio: form.fio.trim(),
    phone: compactPhone(form.phone),
    date: form.date,
    timeSlot: form.timeSlot,
    addressId: form.addressId,
    note: form.note.trim()
  })
}

// ── Reset on open ──
watch(() => props.modelValue, (open) => {
  if (open) {
    step.value = 0
    form.instructorId = props.defaultInstructorId || (props.instructors[0]?.id ?? null)
    form.phone = '+7'
    form.fio = ''
    form.studentId = null
    form.date = props.defaultDate || todayIso.value
    form.timeSlot = ''
    form.addressId = props.addresses.length === 1 ? props.addresses[0].id : null
    form.note = ''
    showAddAddr.value = false
    newAddr.address = ''; newAddr.name = ''
    nextTick(() => phoneEl.value?.focus())
  }
})
</script>

<style scoped>
.wiz { border-radius: 18px; overflow: hidden; display: flex; flex-direction: column; max-height: 88vh; }

.wiz-head { display: flex; align-items: center; gap: 10px; padding: 16px 16px 8px; }
.wiz-head .step-ic {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.16); color: rgb(var(--v-theme-primary));
  display: grid; place-items: center;
}
.wiz-head .t { font-weight: 700; font-size: 1.05rem; flex: 1; }

.dots { display: flex; gap: 6px; padding: 0 18px 12px; }
.dots .d { flex: 1; height: 4px; border-radius: 3px; background: rgba(var(--v-theme-on-surface), 0.12); transition: background .2s; }
.dots .d.on { background: rgb(var(--v-theme-primary)); }

.wiz-body { padding: 4px 18px 14px; min-height: 210px; flex: 1 1 auto; overflow-y: auto; }
.label { font-size: .78rem; color: rgba(var(--v-theme-on-surface), 0.6); margin-bottom: 8px; font-weight: 600; }

.big-input {
  width: 100%; background: rgba(var(--v-theme-on-surface), 0.05);
  border: 2px solid rgba(var(--v-theme-on-surface), 0.16); border-radius: 12px;
  color: rgb(var(--v-theme-on-surface)); padding: 16px; font-size: 1.3rem;
  text-align: center; outline: none; letter-spacing: .5px; font-weight: 600;
}
.big-input:focus { border-color: rgb(var(--v-theme-primary)); }

.chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.16);
  color: rgb(var(--v-theme-on-surface));
  padding: 11px 14px; border-radius: 11px; font-size: 1rem; cursor: pointer;
  font-weight: 600; min-width: 84px; text-align: center;
}
.chip.on { background: rgb(var(--v-theme-primary)); color: rgb(var(--v-theme-on-primary, 0,0,0)); border-color: rgb(var(--v-theme-primary)); }
.chip.taken { opacity: .4; cursor: not-allowed; text-decoration: line-through; }

.date-input, .note-input, .time-input {
  width: 100%; background: rgba(var(--v-theme-on-surface), 0.05);
  border: 2px solid rgba(var(--v-theme-on-surface), 0.16); border-radius: 12px;
  color: rgb(var(--v-theme-on-surface)); padding: 14px; font-size: 1.05rem; outline: none;
  font-family: inherit;
}
.date-input:focus, .note-input:focus, .time-input:focus { border-color: rgb(var(--v-theme-primary)); }
.note-input { min-height: 90px; resize: vertical; }

/* Time row: start — end */
.time-row { display: flex; align-items: flex-end; gap: 10px; }
.time-col { flex: 1; }
.time-sep { font-size: 1.3rem; font-weight: 700; color: rgba(var(--v-theme-on-surface), 0.5); padding-bottom: 14px; flex-shrink: 0; }
.time-input { text-align: center; font-size: 1.15rem; font-weight: 700; }
.time-input::-webkit-calendar-picker-indicator { filter: invert(0.6); cursor: pointer; }

.conflict-warn {
  display: flex; align-items: center; gap: 6px; margin-top: 8px;
  color: rgb(var(--v-theme-error)); font-size: .82rem;
}

/* Quick preset chips (small) */
.preset-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.preset-chip {
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.16);
  color: rgba(var(--v-theme-on-surface), 0.75);
  padding: 6px 12px; border-radius: 8px; font-size: .82rem; cursor: pointer; font-weight: 600;
}
.preset-chip.on { background: rgba(var(--v-theme-primary), 0.18); color: rgb(var(--v-theme-primary)); border-color: rgb(var(--v-theme-primary)); }
.preset-chip.taken { opacity: .35; cursor: not-allowed; text-decoration: line-through; }
.preset-chip:not(.taken):hover { border-color: rgba(var(--v-theme-on-surface), 0.4); }

.addr-list { display: flex; flex-direction: column; gap: 8px; }
.addr-item {
  display: flex; align-items: center; gap: 10px; text-align: left;
  background: rgba(var(--v-theme-on-surface), 0.05);
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.16);
  color: rgb(var(--v-theme-on-surface));
  padding: 13px 14px; border-radius: 11px; font-size: .95rem; cursor: pointer; width: 100%;
}
.addr-item.on { border-color: rgb(var(--v-theme-primary)); background: rgba(var(--v-theme-primary), 0.08); }
.addr-add { color: rgb(var(--v-theme-primary)); border-style: dashed; }
.add-addr-panel { border: 1px dashed rgba(var(--v-theme-on-surface), 0.2); border-radius: 11px; padding: 12px; }

.preview { margin: 0 18px; padding: 12px 14px; background: rgba(var(--v-theme-on-surface), 0.03); border: 1px dashed rgba(var(--v-theme-on-surface), 0.16); border-radius: 12px; }
.preview .pv-label { font-size: .66rem; color: rgba(var(--v-theme-on-surface), 0.5); margin-bottom: 8px; text-transform: uppercase; letter-spacing: .5px; }
.pv-empty { color: rgba(var(--v-theme-on-surface), 0.4); font-size: .82rem; }
.pv-note { display: flex; align-items: flex-start; gap: 11px; }
.pv-note .chk-box { width: 17px; height: 17px; border-radius: 4px; border: 1.5px solid rgba(var(--v-theme-on-surface), 0.45); flex-shrink: 0; margin-top: 2px; }
.pv-line { font-size: .9rem; line-height: 1.55; color: rgba(var(--v-theme-on-surface), 0.92); }
.pv-line span { margin-right: 6px; }
.pv-line .t-phone { color: #85aad6; }
.pv-line .t-note { color: rgba(var(--v-theme-on-surface), 0.55); }

.wiz-foot { display: flex; gap: 10px; padding: 10px 18px 16px; flex-shrink: 0; border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); background: rgb(var(--v-theme-surface)); }
.wiz-head, .dots { flex-shrink: 0; }
</style>
